/**
 * ReviewRequestDialog.tsx
 * =======================
 * 
 * DISCOVERY NOTES (A1):
 * - Found existing RequestReviewPage.tsx (src/pages/RequestReviewPage.tsx) - Full standalone page
 * - Found useReviewRequests.ts (src/hooks/useReviewRequests.ts) - Complete marketplace hooks
 * - Found reviewQueue.ts (functions/src/reviewQueue.ts) - Backend implementation
 * - REUSING: This dialog extracts core form logic from RequestReviewPage for inline use
 * 
 * SRL Review Request Submission Dialog
 * Enables SRL users to submit their case for professional review from within DashboardTab
 * 
 * Required fields from createReviewRequest Cloud Function:
 * - caseId: string
 * - caseTitle: string  
 * - jurisdiction: string (from case or user selection)
 * - requestedProType: 'attorney'
 * - opposingPartyName: string (for conflict check)
 * - note?: string (optional)
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';
import { X, Shield, AlertTriangle, Loader2, Scale, Bell } from 'lucide-react';
import toast from 'react-hot-toast';
import { useMarketplaceAvailability } from '@/hooks/useMarketplaceAvailability';

interface ReviewRequestDialogProps {
  caseId: string;
  caseTitle: string;
  jurisdiction?: string;
  /** WO7-P5-3: Role of the current user in this case. Only 'owner'|'admin'|'editor' may submit. */
  userRole?: 'owner' | 'admin' | 'editor' | 'viewer' | string;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function ReviewRequestDialog({
  caseId,
  caseTitle,
  jurisdiction: initialJurisdiction,
  userRole = 'owner',
  onClose,
  onSuccess,
}: ReviewRequestDialogProps) {
  // WO7-P5-3: RBAC gate — viewers cannot submit review requests
  const canSubmit = userRole === 'owner' || userRole === 'admin' || userRole === 'editor';
  const [proType, setProType] = useState<'attorney' | ''>('attorney');
  const [jurisdiction, setJurisdiction] = useState(initialJurisdiction || '');
  const [opposingPartyName, setOpposingPartyName] = useState('');
  const [note, setNote] = useState('');
  const [legalDisclaimerAccepted, setLegalDisclaimerAccepted] = useState(false);
  const [limitedScopeAccepted, setLimitedScopeAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Check if attorneys are available for this case's jurisdiction.
  // hasAttorneys flips to true automatically once an attorney lists that jurisdiction.
  const { hasAttorneys, loading: availLoading } = useMarketplaceAvailability(initialJurisdiction);

  const handleSubmit = async () => {
    // Validation
    if (!proType) {
      toast.error('Please select Attorney or Paralegal');
      return;
    }

    if (!jurisdiction.trim()) {
      toast.error('Please enter your jurisdiction');
      return;
    }

    if (!opposingPartyName.trim()) {
      toast.error('Please enter the opposing party name(s)');
      return;
    }

    if (!legalDisclaimerAccepted || !limitedScopeAccepted) {
      toast.error('Please accept both required agreements');
      return;
    }

    setSubmitting(true);

    try {
      // Call Cloud Function
      const createReviewRequest = httpsCallable(functions, 'createReviewRequest');
      await createReviewRequest({
        caseId,
        caseTitle,
        jurisdiction: jurisdiction.trim(),
        requestedProType: proType,
        opposingPartyName: opposingPartyName.trim(),
        note: note.trim() || undefined,
      });

      toast.success("Your case has been submitted for legal review. A professional will review it and you'll see status updates here.");
      
      onSuccess?.();
      onClose();
    } catch (error: unknown) {
      // Enhanced error handling for FirebaseError codes
      const isFirebaseError = error && typeof error === 'object' && 'code' in error;
      const errorCode = isFirebaseError ? (error as { code: string }).code : '';
      const errorMessage = isFirebaseError && 'message' in (error as object) ? (error as unknown as { message: string }).message : 'An error occurred';
      
      console.error('[ReviewRequestDialog] Error submitting:', {
        code: errorCode,
        message: errorMessage,
        error,
      });

      // User-friendly error messages based on error codes
      let userMessage = 'Failed to submit review request. Please try again.';
      
      if (errorCode === 'functions/unauthenticated') {
        userMessage = 'You must be logged in to submit a review request.';
      } else if (errorCode === 'functions/permission-denied') {
        userMessage = 'You do not have permission to submit a review for this case.';
      } else if (errorCode === 'functions/invalid-argument') {
        userMessage = errorMessage || 'Please check that all required fields are filled correctly.';
      } else if (errorCode === 'functions/not-found') {
        userMessage = 'Case not found. Please refresh and try again.';
      } else if (errorCode.includes('network') || errorCode.includes('unavailable')) {
        userMessage = 'Network error. Please check your connection and try again.';
      } else if (errorMessage) {
        userMessage = errorMessage;
      }

      toast.error(userMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const allRequirementsMet =
      canSubmit &&
    jurisdiction.trim().length > 0 &&
    opposingPartyName.trim().length > 0 &&
    legalDisclaimerAccepted &&
    limitedScopeAccepted;

  // No attorneys onboarded for this jurisdiction yet — show coming soon instead of the form
  if (availLoading || !hasAttorneys) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="review-request.dialog.overlay">
        <div className="relative w-full max-w-md rounded-lg border border-slate-700 bg-slate-900 p-8 shadow-xl text-center">
          <button
            onClick={onClose}
            aria-label="Close dialog"
            className="absolute top-4 right-4 rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex justify-center mb-5">
            <div className="p-4 bg-slate-800 rounded-full">
              <Scale className="h-8 w-8 text-brand-orange" />
            </div>
          </div>
          {availLoading ? (
            <p className="text-slate-400 text-sm">Checking attorney availability…</p>
          ) : (
            <>
              <h2 className="text-xl font-bold text-slate-100 mb-3">Attorney Review — Coming Soon</h2>
              <p className="text-slate-400 text-sm mb-5 leading-relaxed">
                We're onboarding verified attorneys to the ThreadLock marketplace.
                Once live, you'll be able to submit your case for professional legal
                review directly from here.
              </p>
              <div className="bg-brand-orange/10 border border-brand-orange/30 rounded-lg p-3 flex items-start gap-2 text-left">
                <Bell className="h-4 w-4 text-brand-orange flex-shrink-0 mt-0.5" />
                <p className="text-slate-300 text-xs">
                  You'll be notified at your account email when attorney review is available in your jurisdiction.
                </p>
              </div>
            </>
          )}
          <button
            onClick={onClose}
            className="mt-6 w-full px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="review-request.dialog.overlay">
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg border border-slate-700 bg-slate-900 p-6 shadow-xl" data-testid="review-request.dialog.content">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-100">Request Professional Review</h2>
            <p className="mt-1 text-sm text-slate-400">
              Submit <strong>{caseTitle}</strong> to the professional pool
            </p>
          </div>
          <button
            data-testid="review-request.dialog.close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close dialog"
            className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Professional Type */}
          <div>
            <label className="block text-sm font-semibold text-slate-200">
              Who do you need? <span className="text-red-400">*</span>
            </label>
            <p className="mt-1 text-sm text-slate-400">Select the type of professional review</p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="review-request.pro-type.attorney"
                onClick={() => setProType('attorney')}
                disabled={submitting}
                className={`rounded-lg border-2 p-4 text-left transition ${
                  proType === 'attorney'
                    ? 'border-purple-500 bg-purple-950/50 text-purple-300'
                    : 'border-slate-700 bg-slate-800/50 text-slate-300 hover:border-slate-600'
                }`}
              >
                <Shield className="mb-2 h-6 w-6" />
                <div className="font-semibold">Attorney</div>
                <div className="text-xs text-slate-400">Licensed attorney</div>
              </button>
            </div>
          </div>

          {/* Jurisdiction */}
          <div>
            <label className="block text-sm font-semibold text-slate-200">
              Jurisdiction <span className="text-red-400">*</span>
            </label>
            <p className="mt-1 text-sm text-slate-400">Where is your case being heard?</p>
            <input
              type="text"
              data-testid="review-request.jurisdiction.input"
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              disabled={submitting || !!initialJurisdiction}
              placeholder="e.g., California, New York, Texas"
              className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-2 text-slate-100 placeholder-slate-500 focus:border-purple-500 focus:outline-none disabled:opacity-50"
            />
            {initialJurisdiction && (
              <p className="mt-1 text-xs text-slate-500">Jurisdiction set from case information</p>
            )}
            {/* WO7-P6-3: Explicit notice when jurisdiction was not auto-populated */}
            {!initialJurisdiction && (
              <p className="mt-1 text-xs text-amber-400">Jurisdiction could not be determined from your case — please enter it above.</p>
            )}
          </div>

          {/* Opposing Party Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-200">
              Opposing Party Name(s) <span className="text-red-400">*</span>
            </label>
            <p className="mt-1 text-sm text-slate-400">
              Required for conflict of interest check (not shared publicly)
            </p>
            <input
              type="text"
              data-testid="review-request.opposing-party.input"
              value={opposingPartyName}
              onChange={(e) => setOpposingPartyName(e.target.value)}
              disabled={submitting}
              placeholder="Full legal name(s)"
              className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-2 text-slate-100 placeholder-slate-500 focus:border-purple-500 focus:outline-none"
            />
          </div>

          {/* Optional Note */}
          <div>
            <label className="block text-sm font-semibold text-slate-200">
              Additional Notes <span className="text-slate-500">(Optional)</span>
            </label>
            <textarea
              data-testid="review-request.note.textarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
              placeholder="Any specific details or questions for the reviewer..."
              rows={3}
              className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-2 text-slate-100 placeholder-slate-500 focus:border-purple-500 focus:outline-none"
            />
          </div>

          {/* Required Agreements */}
          <div className="space-y-3 rounded-lg border border-yellow-900/50 bg-yellow-950/20 p-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                data-testid="review-request.legal-disclaimer.checkbox"
                id="legal-disclaimer"
                checked={legalDisclaimerAccepted}
                onChange={(e) => setLegalDisclaimerAccepted(e.target.checked)}
                disabled={submitting}
                className="mt-1 h-4 w-4"
              />
              <label htmlFor="legal-disclaimer" className="text-sm text-slate-300">
                <strong className="text-yellow-400">Legal Disclaimer:</strong> I understand this is for legal
                review only and does not establish an attorney-client relationship.
              </label>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                data-testid="review-request.limited-scope.checkbox"
                id="limited-scope"
                checked={limitedScopeAccepted}
                onChange={(e) => setLimitedScopeAccepted(e.target.checked)}
                disabled={submitting}
                className="mt-1 h-4 w-4"
              />
              <label htmlFor="limited-scope" className="text-sm text-slate-300">
                <strong className="text-yellow-400">Limited Scope:</strong> I understand this is unbundled legal
                assistance for review purposes only.
              </label>
            </div>
          </div>

          {/* WO7-P5-3: RBAC gate notice */}
          {!canSubmit && (
            <div className="mb-4 rounded-lg border border-amber-700/40 bg-amber-900/20 px-4 py-3 text-sm text-amber-300">
              Only the case owner or an editor can submit a review request. Contact the case owner for access.
            </div>
          )}

          {/* Submit Button */}
          <div className="flex items-center justify-between border-t border-slate-800 pt-6">
            <button
              type="button"
              data-testid="review-request.cancel.button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg border border-slate-700 px-4 py-2 text-slate-300 transition hover:bg-slate-800 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="button"
              data-testid="review-request.submit.button"
              onClick={handleSubmit}
              disabled={!allRequirementsMet || submitting}
              className="flex items-center gap-2 rounded-lg bg-purple-600 px-6 py-2 font-semibold text-white transition hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4" />
                  Submit for Review
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
